import { isInViewport } from './utilities';

window.dataLayer = window.dataLayer || [];
const { dataLayer } = window;
const s1 = window.s1 || {};
const blockInjector = s1?.blockInjector || {};
const markupModifier = s1?.markupModifier || {};

/**
 * Function to send an event to Google Tag Manager.
 *
 * @param payload object of data to send to gtm.
 */
export const sendToGtm = (payload) => {
  const defaultValues = {
    event: 'Unified Event',
    eventAction: '',
    eventCategory: '',
    eventLabel: '',
    eventValue: 0,
    nonInteractiveHit: true,
  };

  const newPayload = { ...defaultValues, ...payload };

  dataLayer.push(newPayload);
};

/**
 * Takes in a URL and fire the latter as an image pixel
 *
 * @param url of the image pixel
 */
const fireImgPixel = (url) => {
  const imgEl = new Image();
  imgEl.src = url;
};

/**
 * Tracks ad impressions in the datalayer
 */
const trackAdImpressions = () => {
  const ctaClassToTarget = '.cta-block';

  let impressionListenerStack = document.querySelectorAll(`${ctaClassToTarget}`);
  // Convert from nodelist to array and remove unwanted item from the stack that was picked up by
  // the query.
  impressionListenerStack = [...impressionListenerStack].filter((element) => {
    const { unittype } = element.dataset;
    return unittype === 'cta';
  });

  // No need to add scroll events if the stack is empty.
  if (!impressionListenerStack || !impressionListenerStack.length) return;

  /**
   * loops through the impressionListenerStack array, checks if each element are in the viewport
   * And send to the dataLayer and remove from the array if the element is.
   */
  const process = () => {
    impressionListenerStack.forEach((impElement, index) => {
      // Checks visibility
      if (isInViewport(impElement)) {
        console.log(`Visibility Triggered for ID ${impElement.id}`);

        const trackingObject = {
          event: 'Unified Event',
          eventCategory: 'Ad Event',
          eventAction: 'Ad Impression',
          unittype: impElement.dataset.unittype,
          unitpos: impElement.dataset.unitpos,
          mblock: impElement.dataset.mblock,
          unittheme: impElement.dataset.preset,
          eventrid: markupModifier?.ruleid ?? blockInjector.ruleid,
          experimentvariation: markupModifier?.variationId ?? blockInjector.variationId,
        };

        // Pass to the datalayer
        sendToGtm(trackingObject);
        console.log('Ad Impression pushed to dataLayer', trackingObject);

        // remove from the impression stack array after passing to the datalayer.
        impressionListenerStack.splice(index, 1);
      }
    });

    // Remove event listener if the stack is empty.
    if (!impressionListenerStack || !impressionListenerStack.length) document.removeEventListener('scroll', process);
  };

  // Run on initialization.
  process();

  // Run everytime a scroll event occurs.
  document.addEventListener('scroll', process, { passive: true });
};

/**
 * Tracks ad clicks (CTA and recirc blocks) in the dataLayer
 */
const trackAdClicks = () => {
  const hyperlinks = document.querySelectorAll('a');
  hyperlinks.forEach((link) => {
    const datasets = link.dataset;

    // Return if the a tag doesn't have any dataset.
    if (Object.keys(datasets).length <= 0) return;

    const clickTrackingUrl = datasets.cltr; // data-cltr
    let unittype;
    let unitpos;
    let linkpos;
    let mblock;
    let elementtype;
    let unittheme;

    if (datasets.linkpos) {
      // CTA
      unittype = 'cta';
      linkpos = datasets.linkpos;
    } else if (!clickTrackingUrl) {
      // Not CTA or recirc
      return;
    }

    let parent = link;
    while (parent !== window.document) {
      parent = parent.parentNode;

      if (parent.dataset) {
        linkpos = linkpos || parent.dataset.linkpos;
        unitpos = unitpos || parent.dataset.unitpos;
        mblock = mblock || parent.dataset.mblock;
        unittheme = unittheme || parent.dataset.preset;
        unittype = unittype || parent.dataset.unittype;
      }
    }
    // console.log(unittype, unitpos, linkpos, mblock, elementtype, unittheme);

    // Add click event to CTA link elements
    link.addEventListener('click', (event) => {
      // event.preventDefault();
      const trackingObject = {
        event: 'Unified Event',
        eventCategory: 'Ad Event',
        eventAction: 'Ad Click',
        eventLabel: unittype,
        unittype,
        unitpos,
        linkpos,
        mblock,
        clickClass: elementtype || event.target.className,
        unittheme,
        eventrid: markupModifier?.ruleid ?? blockInjector.ruleid,
        experimentvariation: markupModifier?.variationId ?? blockInjector.variationId,
        nonInteractiveHit: false,
      };

      // Pass to the datalayer
      sendToGtm(trackingObject);
      console.log('Ad Click Event pushed to dataLayer', trackingObject);

      // Fire click tracking pixel
      if (clickTrackingUrl) fireImgPixel(clickTrackingUrl);
    });
  });
};

/**
 * Track Scroll Depth in the datalayer
 */
const trackScrollDepth = () => {
  // Threshold to count scroll depth as an interaction.
  const nonInteractiveDepthThreshold = 50;
  // Thresholds to trigger a datalayer event. The array should be in ascending order for
  // optimization.
  const scrollDepthThresholds = [25, 50, 75, 90];

  /**
   * Returns the percentage of the current scroll depth
   *
   * @returns {number}
   */
  const getScrollDepth = () => {
    const { documentElement, body } = document;
    // Vertical length of the current position from the top of the page
    const scrollHeight = documentElement.scrollTop || body.scrollTop;
    // Vertical length of the page
    const pageHeight = documentElement.scrollHeight || body.scrollHeight;
    // Vertical length of the screen
    const screenHeight = documentElement.clientHeight;

    // return scroll depth as a percentage
    return (scrollHeight / (pageHeight - screenHeight)) * 100;
  };

  /**
   * This function runs everytime a scroll occurs.
   */
  const process = () => {
    const scrollDepth = getScrollDepth();

    while (scrollDepth > scrollDepthThresholds[0]) {
      const threshold = scrollDepthThresholds[0];
      console.log(`${threshold}% scroll threshold triggered`);

      const trackingObject = {
        event: 'Unified Event',
        eventCategory: 'Scroll Depth',
        eventAction: 'Vertical Scroll',
        eventLabel: `${threshold}%`,
        nonInteractiveHit: threshold < nonInteractiveDepthThreshold,
      };

      sendToGtm(trackingObject);
      // remove the first threshold from the array since the event has already been fired.
      scrollDepthThresholds.shift();
    }

    // Unregister scroll event once all the dataLayer events have been fired.
    if (scrollDepthThresholds.length === 0) document.removeEventListener('scroll', process);
  };

  document.addEventListener('scroll', process);
};

/**
 * Log DPL pageload Event.
 */
const logDplPageload = () => {
  const { DPL } = window.s1;
  if (DPL) {
    const userData = window?.s1?.markupModifier || window?.s1?.blockInjector || {};

    const payload = {
      site: window.location.host,
      template: window.s1.wpDataStack?.pageType?.['s1-page-type']?.name || window.s1.wpDataStack?.pageType?.template,
      sessionId: userData?.s1sid || userData?.sid,
    };
    try {
      DPL.events.publishing.publishingPageload.log(payload);
    } catch (e) {
      console.warn('Could not log DPL publishingPageload event');
    }
  } else {
    console.warn('Cannot log DPL Pageload because s1.DPL is undefined');
  }
};

/**
 * Log DPL web vitals.
 */
const logDplWebVitals = () => {
  const { DPL } = window.s1;
  const webVitals = markupModifier?.serverTiming || blockInjector?.serverTiming || null;

  if (!DPL || !webVitals) {
    return;
  }

  try {
    DPL.events.publishing.PublishingWebVitals.log({
      site: window.location.host,
      originDuration: webVitals['origin-duration'] || null,
    });
  } catch (e) {
    console.warn('Could not log DPL PublishingWebVitals event');
  }
};

/**
 * Determines when to fire the pageview event
 */
const trackPageviews = () => {
  // Initial Pageload
  logDplPageload();
  // Virtual Pageview
  window.addEventListener('urlChange', () => {
    logDplPageload();
  });
};

const trackWebVitals = () => {
  window.addEventListener('DPLReady', () => {
    logDplWebVitals();
  });
};

/**
 * Track the time on page
 */
const trackTimeOnPage = () => {
  const trackingObject = {
    event: 'Unified Event',
    eventCategory: 'Time on Page',
    eventAction: '',
    eventLabel: '30 Seconds',
    nonInteractiveHit: false,
  };

  setTimeout(() => {
    console.log('30 seconds on page');
    sendToGtm(trackingObject);
  }, 30000);
};

const init = () => {
  trackPageviews();
  trackWebVitals();
  trackAdImpressions();
  trackAdClicks();
  trackTimeOnPage();
  trackScrollDepth();
};

export default init;
